import { useCallback } from 'react';
import {
  useProjectCollectionFragment,
  useUpdateCollectionNameMutation,
} from '@frameio/graphql-client/generated';
import { useProjectPermissions } from '@frameio/permissions';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { useGetActiveUserId } from '@frameio/shared/hooks/useGetActiveUserId';
import { EditableName, type EditableNameProps } from '../TreeItem/EditableName';
import TEXT from './generated/TEXT/CollectionName';

export function CollectionName({ focused, item, isActive }: EditableNameProps) {
  const projectId = useActiveProjectId();
  const activeUserId = useGetActiveUserId();

  const { data: collection } = useProjectCollectionFragment(item.id);
  const canUpdateCollection =
    (useProjectPermissions({
      check: 'canUpdateCollection',
      projectId,
    }) ||
      activeUserId === collection?.creator?.id) ??
    false;
  const [updateCollectionName] = useUpdateCollectionNameMutation();

  const handleRenameSubmit = useCallback(
    (newName: string) => {
      updateCollectionName({
        optimisticResponse: {
          __typename: 'Mutation',
          updateCollection: {
            __typename: 'UpdateCollectionPayload',
            collection: {
              __typename: 'Collection',
              id: item.id,
              name: newName,
            },
          },
        },
        variables: {
          collectionId: item.id,
          name: newName,
        },
      });
    },
    [item.id, updateCollectionName]
  );

  return (
    <EditableName
      ariaLabel={TEXT.editNameLabel}
      canUpdate={canUpdateCollection}
      focused={focused}
      isActive={isActive}
      item={item}
      onRenameSubmit={handleRenameSubmit}
      originalName={collection.name}
    />
  );
}

TEXT.DEFINE({
  /** Aria label for the inline rename input for the collection */
  editNameLabel: 'Rename collection',
});
